import { TenantFeatureToggle, TenantType } from '@xeetr/types/enums'
import { Controller, useFormContext } from 'react-hook-form'

import { Switch } from '../../components/ui/switch'
import { featureTenantRestrictions } from '../../hooks/common/useFeatureToggle'

const FEATURES: {
  key: TenantFeatureToggle
  label: string
  description: string
  devOnly?: boolean
}[] = [
  {
    key: TenantFeatureToggle.DEVELOPER,
    label: 'Entwicklermodus',
    description:
      'Aktiviert Entwicklerfunktionen und Debugging-Tools. Nicht für den Produktivbetrieb geeignet.',
    devOnly: true,
  },
  {
    key: TenantFeatureToggle.CALENDAR,
    label: 'Kalenderansicht',
    description: 'Ermöglicht erweiterte Kalenderansichten für Buchungen und Terminplanung.',
  },
  {
    key: TenantFeatureToggle.WALKIN,
    label: 'Walk-In Buchungen',
    description: 'Erlaubt Kunden Buchungen ohne vorherige Reservierung (Walk-In Unterstützung).',
  },
  {
    key: TenantFeatureToggle.MULTIPLEBOOKEDTABLES,
    label: 'Mehrfach-Tischbuchung',
    description: 'Erlaubt einer Buchung, mehrere Tische gleichzeitig zu reservieren.',
  },
  {
    key: TenantFeatureToggle.TABLEMANAGEMENT,
    label: 'Tischverwaltung',
    description:
      'Aktiviert erweiterte Funktionen zur Tischverwaltung (z.B. Zusammenlegen, Teilen von Tischen).',
  },
  {
    key: TenantFeatureToggle.CLOSEDSOCIETY,
    label: 'Geschlossene Gesellschaft',
    description: 'Beschränkt Buchungen auf Mitglieder oder eingeladene Gäste.',
  },
  {
    key: TenantFeatureToggle.FOOD_EVENTS,
    label: 'Food Events',
    description: 'Aktiviert Eventmanagement für buchbare Food-Events und Aktionen.',
  },
  {
    key: TenantFeatureToggle.CUSTOMER_ADDRESS,
    label: 'Kundenadresse erforderlich',
    description: 'Kunden müssen bei der Buchung ihre Adresse angeben.',
  },
  {
    key: TenantFeatureToggle.MONTHLY_OVERVIEW,
    label: 'Monatsübersicht',
    description:
      'Zeigt eine Übersicht über Catering, Buchungen, Veranstaltungen und Notizen pro Tag.',
  },
  {
    key: TenantFeatureToggle.CUSTOMER_PAGE,
    label: 'Kundenansicht',
    description:
      'Ermöglicht eine erweitere Kundenansicht zur Bearbeitung der Kundendaten und der zugehörigen Buchungsdaten.',
  },
]

const Features = () => {
  const { control, watch, setValue } = useFormContext()
  const features: TenantFeatureToggle[] = watch('tenant.features') || []
  const tenantType: TenantType = watch('tenant.type')

  const handleToggle = (feature: TenantFeatureToggle, enabled: boolean) => {
    let updated: TenantFeatureToggle[]
    if (enabled) {
      updated = Array.from(new Set([...features, feature]))
    } else {
      updated = features.filter((f) => f !== feature)
    }
    setValue('tenant.features', updated, { shouldDirty: true })
  }

  const filteredFeatures = FEATURES.filter((f) => {
    const allowedTypes = featureTenantRestrictions[f.key]
    return !allowedTypes || allowedTypes.includes(tenantType)
  }).filter((f) => !(f.devOnly && import.meta.env.VITE_ENV === 'p'))

  return (
    <div className="space-y-10">
      <div>
        <h2 className="text-lg font-semibold">Features</h2>
        <p className="text-muted-foreground text-sm">
          Aktivieren oder deaktivieren Sie die gewünschten Funktionen für Ihre Management-Plattform.
        </p>
      </div>
      <div className="space-y-4">
        {filteredFeatures.map(({ key, label, description }) => (
          <div key={key} className="flex flex-col gap-1">
            <div className="flex max-w-xl items-center justify-between">
              <span className="font-medium">{label}</span>
              <Controller
                name="tenant.features"
                control={control}
                render={() => (
                  <Switch
                    checked={features.includes(key)}
                    onCheckedChange={(checked) => handleToggle(key, checked)}
                  />
                )}
              />
            </div>
            <span className="text-muted-foreground pl-1 text-xs">{description}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

export default Features
